import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/app/api';

const initialState = {
  items: [],
  byId: {},
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveError: null,
};

function errMsg(err, fallback) {
  return err.response?.data?.message ?? fallback;
}

export const fetchOwnerEvents = createAsyncThunk(
  'ownerEvents/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/owner/events');
      return data.events ?? [];
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось загрузить события'));
    }
  },
);

export const fetchOwnerEvent = createAsyncThunk(
  'ownerEvents/fetchOne',
  async (eventId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/owner/events/${eventId}`);
      return data.event;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Событие не найдено'));
    }
  },
);

export const createOwnerEvent = createAsyncThunk(
  'ownerEvents/create',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/owner/events', payload);
      return data.event;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось создать событие'));
    }
  },
);

export const updateOwnerEvent = createAsyncThunk(
  'ownerEvents/update',
  async ({ eventId, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/owner/events/${eventId}`, payload);
      return data.event;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось сохранить событие'));
    }
  },
);

export const deleteOwnerEvent = createAsyncThunk(
  'ownerEvents/delete',
  async (eventId, { rejectWithValue }) => {
    try {
      await api.delete(`/owner/events/${eventId}`);
      return eventId;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось удалить событие'));
    }
  },
);

function upsert(state, event) {
  if (!event?.id) return;
  state.byId[event.id] = event;
  const idx = state.items.findIndex((e) => e.id === event.id);
  if (idx >= 0) state.items[idx] = event;
  else state.items.unshift(event);
}

const ownerEventsSlice = createSlice({
  name: 'ownerEvents',
  initialState,
  reducers: {
    clearOwnerEventsError(state) {
      state.error = null;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOwnerEvents.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchOwnerEvent.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchOwnerEvents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
        state.byId = {};
        for (const e of action.payload) {
          if (e?.id) state.byId[e.id] = e;
        }
      })
      .addCase(fetchOwnerEvents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchOwnerEvent.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(fetchOwnerEvent.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createOwnerEvent.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(createOwnerEvent.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        upsert(state, action.payload);
      })
      .addCase(createOwnerEvent.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.saveError = action.payload;
      })
      .addCase(updateOwnerEvent.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(updateOwnerEvent.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        upsert(state, action.payload);
      })
      .addCase(updateOwnerEvent.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.saveError = action.payload;
      })
      .addCase(deleteOwnerEvent.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.map((e) => (e.id === id ? { ...e, delete: true } : e));
        if (state.byId[id]) state.byId[id] = { ...state.byId[id], delete: true };
      });
  },
});

export const { clearOwnerEventsError } = ownerEventsSlice.actions;
export default ownerEventsSlice.reducer;

export const selectOwnerEvents = (state) => state.ownerEvents.items;
export const selectOwnerEventsStatus = (state) => state.ownerEvents.status;
export const selectOwnerEventsError = (state) => state.ownerEvents.error;
export const selectOwnerEventById = (id) => (state) => state.ownerEvents.byId[id];
